import {
  Group,
  Select,
  Stack,
  Tooltip,
  Text,
  ActionIcon,
  Paper,
  Button,
  Flex,
  Collapse,
} from "@mantine/core"
import { DatePickerInput } from "@mantine/dates"
import { UseFormReturnType } from "@mantine/form"
import { useDisclosure } from "@mantine/hooks"
import {
  IconClock,
  IconTimeline,
  IconChartBar,
  IconFilter,
  IconDeviceFloppy,
} from "@tabler/icons-react"
import { usePostHog } from "posthog-js/react"
import { useState, useCallback, useRef } from "react"

import {
  GroupByOptionsType,
  MetricOptionType,
} from "@costory/types/endpoints/DropdownTypes"
import { CurrencyOptions } from "@costory/types/endpoints/shared"
import { AGG_BY_OPTIONS, Filters, DATE_PRESETS } from "@costory/types/filters"
import { Currency, DatePreset } from "@costory/types/prisma-client"

import {
  LIMIT_OPTIONS,
  computeComparisonPeriodDates,
  computePresetDates,
  isGroupByOptional,
} from "@costory/shared/utils/filters"

import { DatePresetSelect } from "@costory/front/components/DatePresetSelect"
import { QueryBuilderWrapper } from "@costory/front/components/QueryBuilderWrapper"
import { ModalCurrency } from "@costory/front/components/charts/ModalCurrency"
import GroupBySelector from "@costory/front/components/dropdown/GroupBySelector"
import MetricSelector from "@costory/front/components/dropdown/MetricSelector"

type Props = {
  form: UseFormReturnType<Filters>
  metricsOptions: MetricOptionType[]
  groupByOptions: GroupByOptionsType
  queryError: boolean
  onSaveView?: () => void
  isNotOtherNode?: boolean
  showComparisonChart?: boolean
  toggleShowComparisonChart?: () => void
  loading?: boolean
  canToggleWhereClause?: boolean
  openSaveModal?: () => void
  defaultShowWhereClause?: boolean
}

export const FilterBar = ({
  form,
  metricsOptions,
  queryError,
  onSaveView,
  isNotOtherNode = true,
  groupByOptions,
  showComparisonChart,
  toggleShowComparisonChart,
  loading = false,
  canToggleWhereClause = false,
  defaultShowWhereClause = false,
  openSaveModal,
}: Props) => {
  const [openedCurrencyModal, currencyModalHandlers] = useDisclosure()
  const [dateRange, setDateRange] = useState<[string | null, string | null]>([
    form.getValues().from,
    form.getValues().to,
  ])
  const [previousDateRange, setPreviousDateRange] = useState<
    [string | null, string | null]
  >([
    form.getValues().previousFrom ?? null,
    form.getValues().previousTo ?? null,
  ])

  const posthog = usePostHog()

  const handleSelectDateRange = useCallback(
    (dateRange: [string | null, string | null]) => {
      const [from, to] = dateRange
      setDateRange([from, to])
      if (from && to) {
        const previous = computeComparisonPeriodDates(from, to, null)
        form.setValues({
          from,
          to,
          datePreset: null,
          ...previous,
        })
        setPreviousDateRange([previous.previousFrom, previous.previousTo])
      }
      posthog.capture("Explorer DateRange Changed", {
        fromDate: from,
        toDate: to,
        previousFromDate: previousDateRange[0],
        previousToDate: previousDateRange[1],
      })
    },
    [form, posthog, previousDateRange],
  )

  const handleSelectDatePreset = useCallback(
    (selectedPreset: string | null | undefined) => {
      if (!selectedPreset) return

      // Validate that the selectedPreset is a valid DatePreset
      if (!DATE_PRESETS.includes(selectedPreset as DatePreset)) {
        console.warn(`Invalid DatePreset: ${selectedPreset}`)
        return
      }

      const presetDates = computePresetDates(selectedPreset as DatePreset)
      setDateRange([presetDates.from, presetDates.to])
      setPreviousDateRange([presetDates.previousFrom, presetDates.previousTo])
      form.setValues({
        ...presetDates,
        datePreset: selectedPreset as DatePreset,
      })
      posthog.capture("Explorer DatePreset Changed", {
        datePreset: selectedPreset as DatePreset,
      })
    },
    [form, posthog],
  )

  const pickerRef = useRef<HTMLButtonElement>(null)

  const setGroupBy = (value: string) => {
    form.setFieldValue("groupBy", value)
    posthog.capture("Explorer GroupBy Changed", {
      groupBy: value,
    })
  }
  const setMetric = (value: string) => {
    form.setFieldValue("metricId", value)
    posthog.capture("Explorer Metric Changed", {
      metricId: value,
    })
  }

  const [isWhereClauseOpen, { toggle: toggleWhereClause }] = useDisclosure(
    defaultShowWhereClause,
  )

  return (
    <>
      <Stack gap="xs">
        <Paper p="md">
          <Group justify="space-between" align="end">
            <Group align="flex-end" wrap="wrap">
              <div data-intercom-target="costory-tour-explorer-metric-selector">
                <MetricSelector
                  metric={form.getValues().metricId}
                  setMetric={setMetric}
                  metricsOptions={metricsOptions}
                  setCurrency={(currency) =>
                    form.setFieldValue("currency", currency)
                  }
                />
              </div>
              <div data-intercom-target="costory-tour-explorer-groupby-selector">
                <GroupBySelector
                  groupBy={form.getValues().groupBy}
                  setGroupBy={setGroupBy}
                  groupByOptions={groupByOptions}
                  whereClause={form.getValues().whereClause}
                  clearable
                />
              </div>
              <DatePresetSelect
                value={form.getValues().datePreset}
                onChange={handleSelectDatePreset}
                from={form.getValues().from}
                to={form.getValues().to}
                onCustomDateChange={handleSelectDateRange}
                customDateRange={dateRange}
                pickerRef={pickerRef as React.RefObject<HTMLButtonElement>}
              />
              <PeriodComparison
                form={form}
                previousDateRange={previousDateRange}
                showComparisonChart={showComparisonChart}
                toggleShowComparisonChart={toggleShowComparisonChart}
              />
              {!onSaveView && (
                <CurrencyPicker
                  form={form}
                  openModal={currencyModalHandlers.open}
                  metricsOptions={metricsOptions}
                />
              )}
              {canToggleWhereClause && (
                <Button
                  onClick={toggleWhereClause}
                  variant={isWhereClauseOpen ? "light" : "transparent"}
                  data-intercom-target="filter-button"
                >
                  <IconFilter />
                </Button>
              )}
            </Group>
            {openSaveModal && (
              <Button
                onClick={openSaveModal}
                leftSection={<IconDeviceFloppy />}
              >
                Save
              </Button>
            )}
            {onSaveView && (
              <Stack>
                <Button
                  leftSection={<IconChartBar size="20" />}
                  size="sm"
                  disabled={!isNotOtherNode || loading}
                  loading={loading}
                  onClick={() => onSaveView && onSaveView()}
                >
                  Save to Digest
                </Button>
              </Stack>
            )}
          </Group>
        </Paper>

        <Collapse in={isWhereClauseOpen || !canToggleWhereClause}>
          <QueryBuilderWrapper
            resetKey={JSON.stringify(form.getValues().whereClause)}
            defaultValue={form.getValues().whereClause}
            queryError={queryError}
            {...form.getInputProps("whereClause")}
          />
        </Collapse>
      </Stack>
      <ModalCurrency
        opened={openedCurrencyModal}
        close={currencyModalHandlers.close}
      />
    </>
  )
}

export const Show = ({ form }: { form: UseFormReturnType<Filters> }) => (
  <Select
    w={70}
    label="Show"
    placeholder="Select limit"
    allowDeselect={false}
    data={LIMIT_OPTIONS}
    {...form.getInputProps("limit")}
    key={form.key("limit")}
    value={form.getValues().limit.toString()}
    onChange={(value) =>
      value ? form.setFieldValue("limit", parseInt(value)) : null
    }
  />
)

export const CurrencyPicker = ({
  form,
  openModal,
  metricsOptions,
}: {
  form: UseFormReturnType<Filters>
  openModal: () => void
  metricsOptions: MetricOptionType[]
}) => {
  const posthog = usePostHog()
  const currentMetric = metricsOptions.find(
    (metric) => metric.id === form.getValues().metricId,
  )
  const tooltipLabel = currentMetric?.onlyUSD
    ? "Currency is not available for this metric: use Cost or Net unblended cost"
    : undefined
  return (
    <Group
      align="center"
      gap="xs"
      justify="start"
      wrap="nowrap"
      w={100}
      data-intercom-target="currency-selector"
    >
      <Flex direction="column">
        <Tooltip label="Show currency exchange rates used">
          <Text
            role="button"
            size="sm"
            style={{ cursor: "pointer", textDecoration: "dotted underline" }}
            onClick={() => openModal()}
          >
            Currency
          </Text>
        </Tooltip>
        <Tooltip label={tooltipLabel} disabled={!tooltipLabel}>
          <Select
            disabled={currentMetric?.onlyUSD ?? false}
            placeholder="Currency"
            allowDeselect={false}
            data={CurrencyOptions}
            {...form.getInputProps("currency")}
            key={form.key("currency")}
            comboboxProps={{}}
            onChange={(value: string | null, _) => {
              if (value && CurrencyOptions.includes(value as Currency)) {
                const currency = value as Currency
                form.setFieldValue("currency", currency)
                posthog.capture("Explorer Currency Changed", {
                  currency,
                })
              }
            }}
          />
        </Tooltip>
      </Flex>
    </Group>
  )
}

export const AggregateBy = ({ form }: { form: UseFormReturnType<Filters> }) => (
  <Select
    w={100}
    label="Aggregate By"
    placeholder="Select aggregation"
    allowDeselect={false}
    data={AGG_BY_OPTIONS}
    {...form.getInputProps("aggBy")}
    key={form.key("aggBy")}
    data-intercom-target="costory-tour-explorer-aggregate-by"
  />
)

const PeriodComparison = ({
  form,
  previousDateRange,
  showComparisonChart,
  toggleShowComparisonChart,
}: {
  form: UseFormReturnType<Filters>
  previousDateRange: [string | null, string | null]
  showComparisonChart?: boolean
  toggleShowComparisonChart?: () => void
}) => {
  const groupByOptional = isGroupByOptional(form.getValues().groupBy)
  // Theme-aware border and background
  const borderColor = "var(--mantine-color-border-0)"
  const iconColor = showComparisonChart
    ? "var(--mantine-color-primaryHighlight-0)"
    : undefined
  const radius = toggleShowComparisonChart ? "0 8px 8px 0" : "8px"
  return (
    <Flex gap={0}>
      {toggleShowComparisonChart && (
        <Tooltip
          label={
            groupByOptional
              ? showComparisonChart
                ? "Hide Overlay Reference Period"
                : "Overlay Reference Period"
              : "Overlay Reference Period is only available when no group by is selected"
          }
        >
          <ActionIcon
            size={36}
            disabled={!groupByOptional}
            style={{
              border: `1px solid ${borderColor}`,
              borderRight: "none",
              borderRadius: "8px 0px 0px 8px",
            }}
            mt="auto"
            variant={showComparisonChart ? "light" : "subtle"}
            color={iconColor}
            onClick={() => {
              toggleShowComparisonChart()
            }}
            data-intercom-target="costory-tour-explorer-toggle-comparison"
          >
            <IconTimeline size={20} />
            <IconClock
              size={12}
              style={{ position: "absolute", top: 1, right: 1 }}
            />
          </ActionIcon>
        </Tooltip>
      )}
      <DatePickerInput
        valueFormat="YYYY MMM DD"
        label="Reference Period"
        type="range"
        w={220}
        value={previousDateRange}
        disabled
        radius={radius}
      />
    </Flex>
  )
}
